import {
  Box,
  Button,
  Flex,
  HStack,
  IconButton,
  SimpleGrid,
  Text,
  Tooltip,
} from "@chakra-ui/react";
import { useEffect, useMemo, useRef, useState } from "react";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";
import { CDS } from "design-system";
import SearchIcon from "design-system/icons/Search";
import XIcon from "design-system/icons/X";
import {
  DEFAULT_ICON,
  DEFAULT_ICON_COLOR,
  PATH_ICON_COLOR_KEYS,
  PATH_ICON_GROUPS,
  PATH_ICON_KEYS,
  PathIconColorKey,
  PathIconGroupKey,
  PathIconKey,
  groupKeyForIcon,
  iconColorPaletteFor,
  isPathIconColorKey,
  isPathIconKey,
} from "features/Path/icons";
import IconTile from "./IconTile";

interface IconPickerProps {
  icon: string | null;
  color: string | null;
  /** When set, shows a preview card at the top with the current icon + this name. */
  previewName?: string;
  onChange: (next: { icon: PathIconKey; color: PathIconColorKey }) => void;
}

export default function IconPicker({ icon, color, previewName, onChange }: IconPickerProps) {
  const selectedIcon: PathIconKey = isPathIconKey(icon) ? icon : DEFAULT_ICON;
  const selectedColor: PathIconColorKey = isPathIconColorKey(color) ? color : DEFAULT_ICON_COLOR;

  // Start on the group that contains the currently-selected icon, so users land where they last were.
  const [activeGroup, setActiveGroup] = useState<PathIconGroupKey>(() => groupKeyForIcon(selectedIcon));
  const [search, setSearch] = useState("");
  const searchQuery = search.trim().toLowerCase();
  const isSearching = searchQuery.length > 0;

  const filteredIcons = useMemo<PathIconKey[] | null>(() => {
    if (!isSearching) return null;
    return (PATH_ICON_KEYS).filter((k) => k.toLowerCase().includes(searchQuery));
  }, [isSearching, searchQuery]);

  // When the controlled icon changes externally (e.g. name-based suggestion), follow it.
  const lastIconRef = useRef(selectedIcon);
  useEffect(() => {
    if (lastIconRef.current !== selectedIcon) {
      lastIconRef.current = selectedIcon;
      setActiveGroup(groupKeyForIcon(selectedIcon));
    }
  }, [selectedIcon]);

  const activeIndex = useMemo(
    () => PATH_ICON_GROUPS.findIndex((g) => g.key === activeGroup),
    [activeGroup],
  );

  // Step prev/next, clamped to bounds.
  const goPrev = () => {
    if (activeIndex > 0) setActiveGroup(PATH_ICON_GROUPS[activeIndex - 1].key);
  };
  const goNext = () => {
    if (activeIndex < PATH_ICON_GROUPS.length - 1) {
      setActiveGroup(PATH_ICON_GROUPS[activeIndex + 1].key);
    }
  };

  // Keep the active tab scrolled into view (so chevron-stepping past a clipped tab brings it visible).
  const tabRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  useEffect(() => {
    const el = tabRefs.current[activeGroup];
    // scrollIntoView is missing in jsdom — optional-chain the call so tests don't blow up.
    el?.scrollIntoView?.({ behavior: "smooth", inline: "center", block: "nearest" });
  }, [activeGroup]);

  return (
    <Box>
      <Text fontSize="xs" fontWeight="600" color="gray.500" textTransform="uppercase" letterSpacing="wider" mb="2">
        Path icon
      </Text>

      {previewName !== undefined && (
        <Flex
          align="center"
          gap="3"
          py="2"
          px="3"
          borderWidth="1px"
          borderColor="gray.200"
          borderRadius="md"
          mb="3"
        >
          <IconTile icon={selectedIcon} color={selectedColor} size={32} />
          <Text fontWeight="500" color="gray.800" noOfLines={1}>
            {previewName || "Path name"}
          </Text>
        </Flex>
      )}

      {/* Search across all icons. When non-empty, replaces the grouped grid below with a flat result. */}
      <Box mb="3">
        <CDS.Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search icons"
          leftIcon={<SearchIcon color="gray.400" boxSize="4" />}
          rightElement={
            isSearching ? (
              <IconButton
                aria-label="Clear search"
                icon={<XIcon boxSize="3.5" />}
                size="xs"
                variant="ghost"
                onClick={() => setSearch("")}
              />
            ) : undefined
          }
        />
      </Box>

      {/* Group nav — chevrons step prev/next, scrollable tab row in the middle */}
      {!isSearching && (
      <HStack spacing="1" mb="3" align="center">
        <IconButton
          aria-label="Previous group"
          icon={<FiChevronLeft />}
          size="xs"
          variant="ghost"
          colorScheme="gray"
          isDisabled={activeIndex === 0}
          onClick={goPrev}
          flexShrink={0}
        />
        <Box
          flex="1"
          minW={0}
          overflowX="auto"
          css={{
            scrollbarWidth: "none",
            "&::-webkit-scrollbar": { display: "none" },
          }}
        >
          <HStack spacing="1" minW="max-content">
            {PATH_ICON_GROUPS.map((group) => {
              const isActive = group.key === activeGroup;
              return (
                <Button
                  key={group.key}
                  ref={(el) => {
                    tabRefs.current[group.key] = el;
                  }}
                  size="xs"
                  variant="ghost"
                  onClick={() => setActiveGroup(group.key)}
                  aria-pressed={isActive}
                  color={isActive ? "cdsIndigo.600" : "gray.500"}
                  bg={isActive ? "cdsIndigo.50" : "transparent"}
                  fontWeight={isActive ? "600" : "500"}
                  _hover={{ bg: isActive ? "cdsIndigo.50" : "gray.100" }}
                  borderRadius="md"
                  px="2"
                  flexShrink={0}
                >
                  {group.label}
                </Button>
              );
            })}
          </HStack>
        </Box>
        <IconButton
          aria-label="Next group"
          icon={<FiChevronRight />}
          size="xs"
          variant="ghost"
          colorScheme="gray"
          isDisabled={activeIndex === PATH_ICON_GROUPS.length - 1}
          onClick={goNext}
          flexShrink={0}
        />
      </HStack>
      )}

      {/* Sliding panels — each group is 5×6 = 30 tiles, side-by-side; we translate the row to bring one into view. */}
      {/* pt/mt trick: selection ring on the top row would be clipped by overflow:hidden; pad inside, pull back outside. */}
      {!isSearching && (
      <Box overflow="hidden" mb="4" pt="1" mt="-1">
        <Flex
          transition="transform 220ms cubic-bezier(0.32, 0.72, 0, 1)"
          transform={`translateX(-${activeIndex * 100}%)`}
        >
          {PATH_ICON_GROUPS.map((group) => (
            <Box key={group.key} flex="0 0 100%" w="100%">
              <SimpleGrid columns={6} spacing="2">
                {group.icons.map((iconKey) => {
                  const isSelected = iconKey === selectedIcon;
                  return (
                    // Outer cell takes the grid spacing; button is tight around the tile only.
                    <Flex key={iconKey} align="center" justify="center">
                      <Tooltip label={iconKey} placement="top" hasArrow openDelay={300}>
                        <Box
                          as="button"
                          type="button"
                          onClick={() => onChange({ icon: iconKey, color: selectedColor })}
                          borderRadius="md"
                          display="inline-flex"
                          w="32px"
                          h="32px"
                          // box-shadow follows border-radius, so the ring hugs the tile.
                          sx={{
                            outline: "none",
                            boxShadow: isSelected
                              ? "0 0 0 2px var(--chakra-colors-cdsIndigo-500)"
                              : "none",
                            "&:focus-visible": {
                              boxShadow: "0 0 0 2px var(--chakra-colors-cdsIndigo-500)",
                            },
                          }}
                          aria-label={`Choose icon: ${iconKey}`}
                          aria-pressed={isSelected}
                        >
                          {/* Selected tile takes on the chosen tint; the rest stay neutral. */}
                          <IconTile
                            icon={iconKey}
                            color={isSelected ? selectedColor : "gray"}
                            size={32}
                          />
                        </Box>
                      </Tooltip>
                    </Flex>
                  );
                })}
              </SimpleGrid>
            </Box>
          ))}
        </Flex>
      </Box>
      )}

      {/* Search-result grid — flat, no group tabs. */}
      {isSearching && (
        <Box mb="4" pt="1" mt="-1">
          {filteredIcons && filteredIcons.length > 0 ? (
            <SimpleGrid columns={6} spacing="2">
              {filteredIcons.map((iconKey) => {
                const isSelected = iconKey === selectedIcon;
                return (
                  <Flex key={iconKey} align="center" justify="center">
                    <Tooltip label={iconKey} placement="top" hasArrow openDelay={300}>
                      <Box
                        as="button"
                        type="button"
                        onClick={() => onChange({ icon: iconKey, color: selectedColor })}
                        borderRadius="md"
                        display="inline-flex"
                        w="32px"
                        h="32px"
                        sx={{
                          outline: "none",
                          boxShadow: isSelected
                            ? "0 0 0 2px var(--chakra-colors-cdsIndigo-500)"
                            : "none",
                          "&:focus-visible": {
                            boxShadow: "0 0 0 2px var(--chakra-colors-cdsIndigo-500)",
                          },
                        }}
                        aria-label={`Choose icon: ${iconKey}`}
                        aria-pressed={isSelected}
                      >
                        <IconTile
                          icon={iconKey}
                          color={isSelected ? selectedColor : "gray"}
                          size={32}
                        />
                      </Box>
                    </Tooltip>
                  </Flex>
                );
              })}
            </SimpleGrid>
          ) : (
            <Text fontSize="sm" color="gray.500" textAlign="center" py="6">
              No icons match “{search.trim()}”.
            </Text>
          )}
        </Box>
      )}

      <Text fontSize="xs" fontWeight="600" color="gray.500" textTransform="uppercase" letterSpacing="wider" mb="2">
        Tint
      </Text>
      <HStack spacing="1.5" wrap="wrap">
        {PATH_ICON_COLOR_KEYS.map((colorKey) => {
          const palette = iconColorPaletteFor(colorKey);
          const isSelected = colorKey === selectedColor;
          return (
            <Tooltip key={colorKey} label={colorKey} placement="top" hasArrow openDelay={300}>
              <Flex
                as="button"
                type="button"
                onClick={() => onChange({ icon: selectedIcon, color: colorKey })}
                w="22px"
                h="22px"
                borderRadius="full"
                bg={palette.bg}
                align="center"
                justify="center"
                borderWidth="2px"
                borderColor={isSelected ? "cdsIndigo.500" : "transparent"}
                aria-label={`Choose color: ${colorKey}`}
                aria-pressed={isSelected}
              >
                <Box w="9px" h="9px" borderRadius="full" bg={palette.fg} />
              </Flex>
            </Tooltip>
          );
        })}
      </HStack>
    </Box>
  );
}
